/**
 * Norse selling-plan utilities — shared by norse-buy-box, selling-plans-alt and
 * norse-subscription-toggle-buy-box. Single JS source of truth for resolving a
 * UI selling plan id to the allocation that is actually purchasable on a variant.
 *
 * Why this exists:
 * - The UI renders plan ids from ONE selling plan group, but bundle variants sell
 *   through their own groups with different plan ids.
 * - Products tagged `force-60-day-sub` lock the UI to a forced-cadence plan that
 *   may live in ANY group. That cadence is ~40 days (same price as the old 60-day
 *   plan). Multi-packs may still approximate the old cadence with "8 week" /
 *   "2 month" names when a 40-day allocation is missing.
 *
 * Resolution order (mirrors snippets/force-60-day-variant-plan.liquid):
 *   1. direct id match on the variant's allocations
 *   2. when the UI plan is ~40-day: "40 day" name match
 *      when the UI plan is ~60-day: exact "60 day" name match, then alias
 *      ("8 week" / "2 month") name match
 *   3. slot-index fallback (Nth plan in group[0] → Nth allocation)
 */
(function () {
  'use strict';
  if (window.NorseSellingPlanUtils) return;

  function is40DayExact(name) {
    const s = String(name || '').toLowerCase();
    return s.includes('40 day') || s.includes('40-day') || s.includes('40days');
  }

  function is60DayExact(name) {
    const s = String(name || '').toLowerCase();
    return s.includes('60 day') || s.includes('60-day') || s.includes('60days');
  }

  function is60DayAlias(name) {
    const s = String(name || '').toLowerCase();
    return (
      s.includes('8 week') || s.includes('8-week') ||
      s.includes('2 month') || s.includes('2-month')
    );
  }

  function is60Day(name) {
    return is60DayExact(name) || is60DayAlias(name);
  }

  /** Find a selling plan by id across ALL groups (forced plans may live outside group[0]). */
  function findPlanById(product, planIdN) {
    if (!Number.isFinite(planIdN)) return null;
    const groups = product?.selling_plan_groups || [];
    for (const group of groups) {
      const hit = (group.selling_plans || []).find(p => Number(p.id) === planIdN);
      if (hit) return hit;
    }
    return null;
  }

  function allocationPlanId(alloc) {
    const sid = alloc?.selling_plan_id ?? alloc?.selling_plan?.id;
    return sid != null ? String(sid) : '';
  }

  /**
   * Plan name for an allocation. `/products/*.js` allocations carry only
   * `selling_plan_id` (no nested plan object), so fall back to a group lookup.
   */
  function allocationPlanName(product, alloc) {
    if (alloc?.selling_plan?.name) return alloc.selling_plan.name;
    const plan = findPlanById(product, Number(allocationPlanId(alloc)));
    return plan?.name || '';
  }

  /**
   * Resolve the allocation on `variant` that corresponds to `uiPlanId`.
   * Returns the allocation object or null.
   */
  function resolveAllocation(product, variant, uiPlanId) {
    if (!variant || uiPlanId == null || uiPlanId === '') return null;
    const planIdN = Number(uiPlanId);
    if (!Number.isFinite(planIdN)) return null;

    const allocs = variant.selling_plan_allocations || [];

    // 1. Direct id match
    const direct = allocs.find(
      a => Number(a.selling_plan_id) === planIdN || Number(a.selling_plan?.id) === planIdN
    );
    if (direct) return direct;

    // 2. Name match when the UI plan is a forced-cadence plan (40-day, else 60-day)
    const uiPlan = findPlanById(product, planIdN);
    if (uiPlan && is40DayExact(uiPlan.name)) {
      const byName = allocs.find(a => is40DayExact(allocationPlanName(product, a)));
      if (byName) return byName;
    } else if (uiPlan && is60Day(uiPlan.name)) {
      const byName =
        allocs.find(a => is60DayExact(allocationPlanName(product, a))) ||
        allocs.find(a => is60DayAlias(allocationPlanName(product, a)));
      if (byName) return byName;
    }

    // 3. Slot-index fallback via group[0]
    const primaryPlans = product?.selling_plan_groups?.[0]?.selling_plans || [];
    const slotIdx = primaryPlans.findIndex(p => Number(p.id) === planIdN);
    if (slotIdx >= 0 && slotIdx < allocs.length) return allocs[slotIdx] || null;

    return null;
  }

  /**
   * Resolve `uiPlanId` to the canonical selling plan id (string) purchasable on
   * `variant`. Falls back to the input id when no allocation matches.
   */
  function canonicalPlanId(product, variant, uiPlanId) {
    if (!uiPlanId) return '';
    const alloc = resolveAllocation(product, variant, uiPlanId);
    return allocationPlanId(alloc) || String(uiPlanId);
  }

  // ---------------------------------------------------------------------------
  // Cart-true allocation cents
  // ---------------------------------------------------------------------------

  /**
   * `/products/{handle}.js` does NOT price a fixed-price plan adjustment the way the cart
   * does in a converted market: it takes a ratio of the LOCAL variant price (€89,99 × 76/99
   * → €68,95) where the cart converts the plan's own price (76 × 0.880931 → €66,95). Both
   * land on the market's .95 rule, so both look like real prices. At rate 1.0 they agree,
   * which is why USD never showed it.
   *
   * Liquid prices allocations the way the cart does, so it publishes the true cents on
   * `[data-ssr-alloc-cents]` and every buy box prefers them over anything it fetches.
   * Keys are the allocation's OWN plan id — not the UI plan id, which differs on bundles.
   * Merging across elements is safe: variant ids are unique store-wide.
   */
  let ssrAllocCache = null;
  function allocCents(variantId, planId) {
    if (ssrAllocCache === null) {
      ssrAllocCache = {};
      document.querySelectorAll('[data-ssr-alloc-cents]').forEach(el => {
        try {
          Object.assign(ssrAllocCache, JSON.parse(el.getAttribute('data-ssr-alloc-cents') || '{}'));
        } catch {
          /* a malformed attribute must not take the price down with it */
        }
      });
    }
    const cents = ssrAllocCache?.[String(variantId)]?.[String(planId)];
    return Number.isFinite(cents) && cents > 0 ? cents : null;
  }

  /** Cart-true cents for an allocation object, falling back to its fetched price. */
  function allocationPrice(variantId, alloc) {
    const planId = alloc?.selling_plan_id ?? alloc?.selling_plan?.id;
    const ssr = planId != null ? allocCents(variantId, planId) : null;
    return ssr != null ? ssr : alloc?.price ?? null;
  }

  // ---------------------------------------------------------------------------
  // Money — one formatter for every buy box
  // ---------------------------------------------------------------------------

  /**
   * Currency punctuation for the current market, derived from `data-money-sample`:
   * 1.234.567,89 put through the same Liquid filter the price nodes use.
   *
   * Deriving beats `Intl`: the page locale is English while the market's currency keeps
   * its own separators, so `Intl.NumberFormat('en-US', …)` writes "€66.95" — or worse,
   * "SEK 774" — next to Liquid's "€66,95" on the same page.
   */
  let moneyShapeCache = null;
  function moneyShape() {
    if (moneyShapeCache) return moneyShapeCache;
    const sample = document.querySelector('[data-money-sample]')?.getAttribute('data-money-sample') || '';
    const digits = sample.match(/\d[\d.,\u00A0\u202F ]*\d/);
    if (!digits) return null;

    const raw = digits[0];
    // Only a separator followed by exactly two trailing digits is a decimal point, so a
    // zero-decimal currency ("1.234.568 Ft") does not get its last group read as cents.
    const dec = raw.match(/([.,])(\d{2})$/);
    const intPart = dec ? raw.slice(0, -3) : raw;
    moneyShapeCache = {
      prefix: sample.slice(0, digits.index),
      suffix: sample.slice(digits.index + raw.length),
      group: (intPart.match(/[.,\u00A0\u202F ]/) || [''])[0],
      decimal: dec ? dec[1] : '',
      decimals: dec ? 2 : 0,
    };
    return moneyShapeCache;
  }

  /**
   * Cents → the market's money string, matching Liquid's `money_without_trailing_zeros`
   * so server render and client repaint agree.
   *
   * Never rounds to whole units. That used to be done "for display parity with the cart",
   * and it is a no-op in USD because every shop-currency price is whole — but in every
   * converted market it printed a price nobody is charged: €66,95 in the cart against
   * "€67" (and, via the old Intl fallback, "€69") in the buy box.
   */
  function formatMoney(cents) {
    const shape = moneyShape();
    const n = Math.abs(Math.round(Number(cents) || 0));
    if (!shape) {
      const plain = (n / 100).toFixed(2).replace(/\.00$/, '');
      return (Number(cents) < 0 ? '-' : '') + '$' + plain;
    }

    const whole = shape.decimals === 2 ? Math.floor(n / 100) : Math.round(n / 100);
    const fraction = shape.decimals === 2 ? String(n % 100).padStart(2, '0') : '';

    let out = String(whole);
    if (shape.group) out = out.replace(/\B(?=(\d{3})+(?!\d))/g, shape.group);
    if (fraction && fraction !== '00') out += shape.decimal + fraction;
    return (Number(cents) < 0 ? '-' : '') + shape.prefix + out + shape.suffix;
  }

  window.NorseSellingPlanUtils = {
    is40DayExact,
    is60DayExact,
    is60DayAlias,
    is60Day,
    findPlanById,
    resolveAllocation,
    canonicalPlanId,
    allocCents,
    allocationPrice,
    formatMoney,
  };
})();
